import logger from '@salesforce/retail-react-app/app/utils/logger-instance'
import {DEFAULT_MAX_ORDER_QUANTITY} from '../constants'
/*
 * Copyright (c) 2024, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

/**
 * Get the human-friendly version of the variation values that users have selected.
 * Useful for displaying these values in the UI.
 *
 * @param {Object} variationAttributes - The products variation attributes.
 * @param {Object} values - The variations selected attribute values.
 * @returns {Object} - A key value map of the display name and display value.
 *
 * @example
 * const displayValues = getDisplayVariationValues(
 *     [ { "id": "color", "name": "Colour", "values": [ { "name": "royal", "orderable": true, "value": "JJ5FUXX" } ] } ],
 *     { "color": "JJ5FUXX" }
 * )
 * // returns { "Colour": "royal" }
 */
export const getDisplayVariationValues = (variationAttributes, values = {}) => {
    let returnVal = []
    if (variationAttributes) {
        returnVal = Object.entries(values).reduce((acc, [id, value]) => {
            const attribute = variationAttributes.find(({id: attributeId}) => attributeId === id)
            const attributeValue = attribute.values.find(
                ({value: attributeValue}) => attributeValue === value
            )
            return {
                ...acc,
                [attribute.name]: attributeValue.name
            }
        }, {})
    }
    return returnVal
}

/**
 * Returns the max order quantity for a product.
 *
 * @param {Object} product - Product object
 * @param {Object} configs - Config object with app preferences
 * @returns {number} Max order quantity
 */

export const getMaxOrderQuantity = (product, configs) => {
    const customPreferenceMaxOrderQuantity = configs?.app?.library?.maxOrderQuantity
    const maxOrderQuantity = product?.c_maxOrderQuantity
        ? product?.c_maxOrderQuantity
        : customPreferenceMaxOrderQuantity
        ? Number(customPreferenceMaxOrderQuantity)
        : DEFAULT_MAX_ORDER_QUANTITY

    return maxOrderQuantity
}
/**
 * Determines the appropriate sash based on inventory availability.
 *
 * @param {Object} productDetails - The product object containing inventory data.
 * @param {boolean} isComingSoon - Flag indicating if the product has a future street date.
 * @param {boolean} showComingSoonSash - Whether to force-show the "Coming Soon" sash.
 * @returns {Array<{name: string, msg: string}>} Array of sash objects to be applied.
 *
 * @example
 * getAutoSash(product, false, true)
 * // Returns [{ name: 'instock', msg: 'In-Stock' }] or other status depending on inventory
 */
export const getAutoSash = (productDetails, isComingSoon, showComingSoonSash) => {
    const sashes = []
    const inventory = productDetails?.inventory
    const ATS = inventory?.ats ?? 0

    try {
        if (!inventory) return sashes

        const isInStock = inventory.stockLevel > 0 && inventory.orderable
        if (isInStock && ATS > 0 && !showComingSoonSash) {
            sashes.push({name: 'instock', msg: 'In-Stock'})
        } else if (ATS === 0 && !isComingSoon && !showComingSoonSash) {
            sashes.push({name: 'soldout', msg: 'Sold-Out'})
        } else if (inventory.backorderable && ATS > 0 && inventory.stockLevel == 0) {
            sashes.push({name: 'backorder', msg: 'Back-Order'})
        } else if (inventory.preorderable && ATS !== 0 && !showComingSoonSash) {
            sashes.push({name: 'preorder', msg: 'Pre-Order'})
        } else if ((showComingSoonSash || isComingSoon) && ATS === 0) {
            sashes.push({name: 'comingsoon', msg: 'Coming Soon'})
        }
    } catch (error) {
        logger.error('getAutoSash: exception occurred = ' + error.message)
    }

    return sashes
}

/**
 * Check if product can be added to cart without exceeding available stock
 * @param {Object} product - product details
 * @param {number} quantity - quantity to add
 * @param {Object} basket - current basket object
 * @param {Function} showToast - function to show toast messages
 * @param {Function} formatMessage - function to format i18n messages
 * @returns {boolean} - true if valid, false otherwise
 */
export const canAddToCart = (
    product,
    quantity,
    basket,
    maxOrderQuantity,
    showToast,
    formatMessage
) => {
    if (!product || !quantity) {
        return false
    }
    const {id: productId, inventory, name} = product
    const item = basket?.productItems?.find((item) => item.productId === productId)
    if (!item) {
        //stop processing inventory logic if product is not there on basket
        return true
    }
    const ats = inventory?.ats ?? 0
    const cartQuantity = item ? item.quantity : 0
    const totalQuantity = cartQuantity + quantity

    if (totalQuantity > ats) {
        showToast({
            title: formatMessage(
                {
                    id: 'product.addtocart.exceed.error',
                    defaultMessage:
                        'Only "{ats}" items in stock. Selected quantity for "{productName}" cannot be added to the cart'
                },
                {ats, productName: name}
            ),
            status: 'error',
            isClosable: true,
            duration: 1000
        })
        return false
    } else if (totalQuantity > maxOrderQuantity) {
        showToast({
            title: formatMessage(
                {
                    id: 'product.addtocart.maxOrderQuantity.error',
                    defaultMessage: 'Max "{maxOrderQuantity}" quantity can be added to the cart'
                },
                {maxOrderQuantity}
            ),
            status: 'error',
            isClosable: true,
            duration: 1000
        })
        return false
    }
    return true
}

/**
 * Calculates inventory-related counts for a list of products or variants.
 *
 * @param {Array<Object>} items - List of products or variants.
 * @returns {Object} Inventory counts including instock, preorder, soldout, backorder, and onlineCount.
 *
 * @example
 * handleCounts([{inventory: {ats: 10, preorderable: false, backorderable: false}}])
 * // Returns { instock: 1, preOrder: 0, soldout: 0, backorder: 0, onlineCount: 0 }
 */
export const handleCounts = (items) => {
    let instock = 0,
        preOrder = 0,
        soldout = 0,
        backorder = 0,
        onlineCount = 0

    items.forEach((item) => {
        const inventory = item?.product?.inventory || item?.inventory
        const ats = inventory?.ats ?? 0

        if (!inventory) {
            soldout++
        } else {
            const isInStock = inventory.stockLevel > 0 && inventory.orderable
            if (isInStock && ats > 0) instock++
            else if (ats === 0) soldout++
            else if (inventory.preorderable) preOrder++
            else if (inventory.backorderable) backorder++

            if (inventory.orderable) onlineCount++
        }
    })

    return {instock, preOrder, soldout, backorder, onlineCount}
}

/**
 * Applies appropriate sash(es) for master or bundle products based on inventory counts.
 *
 * @param {number} instock - Count of in-stock items.
 * @param {number} preOrder - Count of pre-orderable items.
 * @param {number} soldout - Count of sold-out items.
 * @param {number} backorder - Count of backorderable items.
 * @param {Array<Object>} sashes - The array to push new sash objects into.
 * @param {Array<Object>} items - Array of variant or bundled product items.
 * @param {string} hideSashes - A configuration string to conditionally hide specific sashes.
 * @param {string} productType - Either 'bundle' or 'master'.
 * @param {boolean} isComingSoon - Indicates if the product release date is in the future.
 * @param {number} onlineCount - Count of items available for online ordering.
 * @param {Function} pushSash - A callback function to push sash objects into `sashes`.
 *
 * @example
 * getSashForMasterAndOtherProducts(1, 0, 0, 0, [], items, 'IN-STOCK-NO', 'master', false, 1, pushSash)
 * // Pushes {name: 'instock', msg: 'In-Stock'} to sashes
 */
export const getSashForMasterAndOtherProducts = (
    instock,
    preOrder,
    soldout,
    backorder,
    sashes,
    items,
    hideSashes,
    productType,
    isComingSoon,
    onlineCount,
    pushSash
) => {
    if (productType === 'bundle') {
        if (soldout >= 1 && hideSashes !== 'SOLD-OUT-NO') pushSash('soldout', 'Sold-Out')
        else if (preOrder > 0 && instock > 0 && hideSashes !== 'PRE-ORDER-NO')
            pushSash('preorder', 'Pre-Order')
        else if (backorder > 0 && instock > 0) pushSash('backorder', 'Back-Order')
        else if (backorder > 0 && preOrder > 0) pushSash('preorder', 'Pre-Order')
        else if (instock === items.length && hideSashes !== 'IN-STOCK-NO')
            pushSash('instock', 'In-Stock')
        else if (backorder === items.length) pushSash('backorder', 'Back-Order')
        else if (preOrder === items.length && hideSashes !== 'PRE-ORDER-NO')
            pushSash('preorder', 'Pre-Order')
    } else {
        if (instock >= 1 && hideSashes !== 'IN-STOCK-NO') pushSash('instock', 'In-Stock')
        else if (soldout === items.length && hideSashes !== 'SOLD-OUT-NO' && !isComingSoon)
            pushSash('soldout', 'Sold-Out')
        else if (preOrder >= 1 && hideSashes !== 'PRE-ORDER-NO') pushSash('preorder', 'Pre-Order')
        else if (backorder > 0 && backorder === onlineCount) pushSash('backorder', 'Back-Order')
        else if (soldout === items.length && isComingSoon && hideSashes !== 'Coming-SOON-NO')
            pushSash('comingsoon', 'Coming Soon')
    }
}

/**
 * Determines the cart button text based on product type, update status, and sash priority.
 *
 * @param {Object} options - Configuration object.
 * @param {boolean} options.updateCart - Whether the cart item is being updated.
 * @param {boolean} options.isProductASet - If the product is a set.
 * @param {boolean} options.isProductABundle - If the product is a bundle.
 * @param {Array<Object>} options.sashes - List of sash objects with name/msg.
 * @param {Object} options.buttonText - Dictionary of button text labels.
 * @returns {string} - The appropriate label for the Add to Cart button.
 *
 * // Returns: 'Update'
 */
export const getCartButtonText = ({
    updateCart,
    isProductASet,
    isProductABundle,
    sashes,
    buttonText
}) => {
    if (updateCart) return buttonText.update
    if (isProductASet) return buttonText.addSetToCart
    if (isProductABundle) return buttonText.addBundleToCart

    const priority = ['preorder', 'backorder', 'instock']
    const primarySash = sashes?.find((sash) => priority.includes(sash?.name))?.name || 'instock'

    switch (primarySash) {
        case 'preorder':
            return buttonText.preorder
        case 'backorder':
            return buttonText.backorder
        case 'instock':
        default:
            return buttonText.addToCart
    }
}

export * from '@salesforce/retail-react-app/app/utils/product-utils'
